import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { http, fmtGBP, fmtDateShort } from "@/lib/api";
import { StatusBadge, PriorityBadge } from "@/components/StatusBadge";
import { Plus, MagnifyingGlass, Star, FunnelSimple, X, ListChecks } from "@phosphor-icons/react";

const STATUSES = ["", "submitted", "pending_approval", "approved", "assigned", "in_progress", "paused", "returned", "completed", "closed"];
const PRIORITIES = ["", "low", "normal", "high", "urgent"];
const JOB_TYPES = [["", "All types"], ["equipment_repair", "Equipment Repair"], ["building_repair", "Building Repair"]];

export default function Jobs() {
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [jobType, setJobType] = useState("");
  const [priority, setPriority] = useState("");

  const params = {};
  if (status) params.status = status;
  if (assigneeId) params.assigned_to_id = assigneeId;
  if (jobType) params.job_type = jobType;
  if (priority) params.priority = priority;

  const { data: jobs = [] } = useQuery({
    queryKey: ["jobs", status, assigneeId, jobType, priority],
    queryFn: () => http.get("/jobs", { params }).then(r => r.data),
  });
  const { data: stores = [] } = useQuery({ queryKey: ["stores"], queryFn: () => http.get("/stores").then(r => r.data) });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => http.get("/users").then(r => r.data) });
  const storeMap = Object.fromEntries(stores.map(s => [s.id, s.name]));
  const userMap = Object.fromEntries(users.map(u => [u.id, u.name]));
  const techs = users.filter(u => ["maintenance_staff", "contractor"].includes(u.role));

  const filtered = useMemo(() =>
    jobs.filter(j => !q || j.title.toLowerCase().includes(q.toLowerCase()) || j.job_number.toLowerCase().includes(q.toLowerCase())),
    [jobs, q]);

  const hasActiveFilter = !!(assigneeId || jobType || priority);
  const clearAll = () => { setAssigneeId(""); setJobType(""); setPriority(""); };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="overline">All Jobs</div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight mt-1">Jobs</h1>
        </div>
        <Link to="/jobs/new" data-testid="jobs-create-button"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-white text-sm font-medium" style={{ background: "#002FA7" }}>
          <Plus size={14} weight="bold" /> New job
        </Link>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="jobs-search-input" value={q} onChange={e => setQ(e.target.value)} placeholder="Search title or job number…"
            className="h-9 pl-8 pr-3 text-sm border border-slate-200 rounded-md w-72 outline-none focus:border-[#002FA7]" />
        </div>
        <div className="flex items-center gap-1 ml-2 flex-wrap">
          {STATUSES.map((s) => (
            <button key={s || "all"} data-testid={`jobs-filter-${s || "all"}`} onClick={() => setStatus(s)}
              className={`h-9 px-3 text-xs font-heading uppercase tracking-wider border rounded-sm transition-colors ${
                status === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              }`}>
              {s ? s.replace("_", " ") : "All"}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4 pb-3 border-b border-slate-200">
        <FunnelSimple size={14} className="text-slate-500" />
        <select value={jobType} onChange={(e) => setJobType(e.target.value)} data-testid="jobs-type-filter"
          className="h-9 px-3 text-sm border border-slate-200 rounded-md outline-none focus:border-[#002FA7] bg-white">
          {JOB_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="jobs-priority-filter"
          className="h-9 px-3 text-sm border border-slate-200 rounded-md outline-none focus:border-[#002FA7] bg-white">
          <option value="">All priorities</option>
          {PRIORITIES.slice(1).map((p) => <option key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</option>)}
        </select>
        <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} data-testid="jobs-assignee-filter"
          className="h-9 px-3 text-sm border border-slate-200 rounded-md outline-none focus:border-[#002FA7] bg-white">
          <option value="">All assignees</option>
          <option value="__unassigned__" disabled>(use status filter for unassigned)</option>
          {techs.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        {hasActiveFilter && (
          <button onClick={clearAll} data-testid="jobs-filters-clear"
            className="inline-flex items-center gap-1 h-9 px-3 text-[11px] text-slate-500 hover:text-rose-600">
            <X size={11} /> Clear filters
          </button>
        )}
      </div>

      <div className="rmms-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] font-heading uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="px-4 py-2.5">Job #</th>
              <th className="px-4 py-2.5">Title</th>
              <th className="px-4 py-2.5">Type</th>
              <th className="px-4 py-2.5">Store</th>
              <th className="px-4 py-2.5">Assignee</th>
              <th className="px-4 py-2.5">Priority</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5 text-right">Est. Cost</th>
              <th className="px-4 py-2.5">Due</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((j) => (
              <tr key={j.id} data-testid={`job-row-${j.job_number}`} className="hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3 font-mono-data text-[12px] text-slate-600">
                  <Link to={`/jobs/${j.id}`} className="hover:text-[#002FA7]" data-testid={`job-link-${j.job_number}`}>{j.job_number}</Link>
                </td>
                <td className="px-4 py-3"><Link to={`/jobs/${j.id}`} className="font-medium hover:text-[#002FA7]">{j.title}</Link></td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-heading uppercase tracking-wider font-semibold border rounded-sm ${
                    j.job_type === "equipment_repair" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-slate-50 text-slate-600 border-slate-200"
                  }`}>
                    {j.job_type === "equipment_repair" ? "Equipment" : "Building"}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-600">{storeMap[j.store_id] || "—"}</td>
                <td className="px-4 py-3 text-slate-600 text-[12px]">{userMap[j.assigned_to_id] || <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-3"><PriorityBadge priority={j.priority} /></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusBadge status={j.status} />
                    {j.stage_summary && (
                      <span data-testid={`job-stages-${j.job_number}`}
                        title={`${j.stage_summary.done} done, ${j.stage_summary.in_progress} in progress`}
                        className={`inline-flex items-center gap-1 text-[11px] font-mono-data px-1.5 py-0.5 border rounded-sm ${
                          j.stage_summary.done === j.stage_summary.total
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : "bg-slate-50 text-slate-600 border-slate-200"
                        }`}>
                        <ListChecks size={10} weight="bold" />
                        {j.stage_summary.done}/{j.stage_summary.total}
                      </span>
                    )}
                    {j.feedback_rating && (
                      <span data-testid={`job-rating-${j.job_number}`} className="inline-flex items-center gap-0.5 text-[11px] font-mono-data text-amber-700">
                        <Star size={11} weight="fill" color="#F59E0B" />
                        {j.feedback_rating}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-right font-mono-data tabular-nums">{fmtGBP(j.estimated_cost)}</td>
                <td className="px-4 py-3 font-mono-data text-[12px] text-slate-600">{fmtDateShort(j.due_date)}</td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={9} className="px-4 py-12 text-center text-sm text-slate-500">No jobs match these filters.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
